import { snakeToCamelCase, isNotEmpty, isPresent, humanize } from "neetocist";
import { identity, isEmpty, indexBy, omit, prop } from "ramda";

import {
  ACTIONS,
  DATE_FORMAT_OPTIONS_MAP,
  FIELD_NAME_MAP,
  INTEGRATION_DISPLAY_NAME_MAP,
  LANGUAGE_OPTIONS_MAP,
} from "./constants";
import Trans from "./Trans";

const formatFieldName = field => FIELD_NAME_MAP[field] || humanize(field);

const createFieldValueFormatter = ({ field, countriesMap }) => {
  switch (field) {
    case "language":
      return value => LANGUAGE_OPTIONS_MAP[value]?.label || value;
    case "dateFormat":
      return value => DATE_FORMAT_OPTIONS_MAP[value] || value;
    case "countryIdentifier":
      return value => countriesMap[value]?.name || value;
    default:
      return identity;
  }
};

const formatChangedFields = ({ changes, countriesMap }) => {
  const fields = Object.keys(changes).map((field, index) => {
    const change = changes[field];
    const valueFormatter = createFieldValueFormatter({ field, countriesMap });
    let element;

    if (field === "profileImage") {
      element = (
        <span key={field}>
          <Trans
            additionalValues={{ field: formatFieldName(field) }}
            i18nKey="neetoMolecules.auditLogs.text.updatedFields"
          />
        </span>
      );
    } else if (change && isPresent(change.from)) {
      element = (
        <span key={field}>
          <Trans
            i18nKey="neetoMolecules.auditLogs.text.updatedFieldsWithPreviousValue"
            additionalValues={{
              field: formatFieldName(field),
              from: valueFormatter(change.from),
              to: valueFormatter(change.to),
            }}
          />
        </span>
      );
    } else {
      element = (
        <span key={field}>
          <Trans
            i18nKey="neetoMolecules.auditLogs.text.updatedFieldsWithNewValue"
            additionalValues={{
              field: formatFieldName(field),
              to: valueFormatter(change.to),
            }}
          />
        </span>
      );
    }

    return index > 0
      ? [<span key={`sep-${field}`}>, </span>, element]
      : element;
  });

  return fields.flat();
};

export const transformAction = action =>
  snakeToCamelCase(action.replace("audit_logs.organization.", ""));

export const constructAuditLogText = ({
  countriesMap = {},
  permissionsMap = {},
  action,
  performedBy,
  emails: emailsProp,
  firstName,
  lastName,
  organizationRole,
  email,
  changes = {},
  status,
  message,
  errorMessage,
  ...rest
}) => {
  const emails = emailsProp ?? [];
  switch (action) {
    case ACTIONS.INVITED_USERS: {
      if (status === "success" && isNotEmpty(emails)) {
        const emailList = emails.join(", ");

        return (
          <Trans
            additionalValues={{ performedBy, emailList }}
            i18nKey="neetoMolecules.auditLogs.text.invitedUsers"
          />
        );
      }

      return (
        <Trans
          additionalValues={{ performedBy, errorMessage }}
          i18nKey="neetoMolecules.auditLogs.text.invitedUsersFailed"
        />
      );
    }

    case ACTIONS.USER_SIGNED_UP: {
      const name = [firstName, lastName].filter(Boolean).join(" ");
      const userIdentifier = name || organizationRole || email || "User";

      return (
        <Trans
          additionalValues={{ userIdentifier }}
          i18nKey="neetoMolecules.auditLogs.text.userSignedUp"
        />
      );
    }

    case ACTIONS.UPDATED_USER: {
      const affectedUser = email || "user";
      const isUserDeactivated = changes?.active?.to === false;
      const otherChanges = isUserDeactivated
        ? omit(["active"], changes)
        : changes;

      const removedText = isUserDeactivated ? (
        <Trans
          additionalValues={{ performedBy, affectedUser }}
          i18nKey="neetoMolecules.auditLogs.text.removedUser"
        />
      ) : null;

      if (isUserDeactivated && isEmpty(otherChanges)) return removedText;

      const changedFields = formatChangedFields({
        changes: otherChanges,
        countriesMap,
      });

      const updatedUserText = (
        <Trans
          additionalComponents={{ ChangedFields: <>{changedFields}</> }}
          additionalValues={{ performedBy, affectedUser }}
          i18nKey="neetoMolecules.auditLogs.text.updatedUser"
        />
      );

      return removedText ? (
        <>
          {removedText} {updatedUserText}
        </>
      ) : (
        updatedUserText
      );
    }

    case ACTIONS.BULK_UPDATE_USERS: {
      if (status === "success" && isNotEmpty(emails)) {
        const emailList = emails.join(", ");

        return (
          <Trans
            additionalValues={{ performedBy, emailList, message }}
            i18nKey="neetoMolecules.auditLogs.text.bulkUpdateUsers"
          />
        );
      }

      return (
        <Trans
          additionalValues={{ performedBy, errorMessage }}
          i18nKey="neetoMolecules.auditLogs.text.bulkUpdateUsersFailed"
        />
      );
    }

    case ACTIONS.UPDATED_USER_PROFILE: {
      const changedFields = formatChangedFields({ changes, countriesMap });

      return (
        <Trans
          additionalComponents={{ ChangedFields: <>{changedFields}</> }}
          additionalValues={{ performedBy }}
          i18nKey="neetoMolecules.auditLogs.text.updatedUserProfile"
        />
      );
    }

    case ACTIONS.CREATED_ROLE: {
      const roleName = organizationRole || "role";

      return (
        <Trans
          additionalValues={{ performedBy, roleName }}
          i18nKey="neetoMolecules.auditLogs.text.createdRole"
        />
      );
    }

    case ACTIONS.UPDATED_ROLE: {
      const { addedPermissions = [], removedPermissions = [], name } = rest;

      const permissionsToText = permissions =>
        permissions
          .map(
            permission =>
              permissionsMap[permission]?.description ||
              humanize(permission.split(".")[1])
          )
          .join(", ");

      let i18nKey = "neetoMolecules.auditLogs.text.updatedRole";

      if (name) {
        i18nKey = "neetoMolecules.auditLogs.text.updatedRoleName";
      } else if (isPresent(addedPermissions) && isPresent(removedPermissions)) {
        i18nKey = "neetoMolecules.auditLogs.text.modifiedRolePermission";
      } else if (isPresent(addedPermissions)) {
        i18nKey = "neetoMolecules.auditLogs.text.addedRolePermission";
      } else if (isPresent(removedPermissions)) {
        i18nKey = "neetoMolecules.auditLogs.text.removedRolePermission";
      }

      return (
        <Trans
          {...{ i18nKey }}
          additionalValues={{
            performedBy,
            roleName: organizationRole || "role",
            name,
            addedPermissionsText: permissionsToText(addedPermissions),
            removedPermissionsText: permissionsToText(removedPermissions),
            addedPermissionsCount: addedPermissions.length,
            removedPermissionsCount: removedPermissions.length,
          }}
        />
      );
    }

    case ACTIONS.INTEGRATION_CONNECTED: {
      const { integrationName: rawName, integrationOwner } = rest;
      const integrationName =
        INTEGRATION_DISPLAY_NAME_MAP[rawName] || humanize(rawName);
      const detail = changes?.detail;
      let i18nKey;

      if (integrationOwner && detail) {
        i18nKey = "neetoMolecules.auditLogs.text.integrationConnectedOnBehalf";
      } else if (integrationOwner) {
        i18nKey =
          "neetoMolecules.auditLogs.text.integrationConnectedOnBehalfWithoutDetail";
      } else if (detail) {
        i18nKey = "neetoMolecules.auditLogs.text.integrationConnected";
      } else {
        i18nKey =
          "neetoMolecules.auditLogs.text.integrationConnectedWithoutDetail";
      }

      return (
        <Trans
          {...{ i18nKey }}
          additionalValues={{
            performedBy,
            integrationName,
            detail,
            integrationOwner,
          }}
        />
      );
    }

    case ACTIONS.INTEGRATION_DISCONNECTED: {
      const { integrationName: rawName, integrationOwner } = rest;
      const integrationName =
        INTEGRATION_DISPLAY_NAME_MAP[rawName] || humanize(rawName);
      const detail = changes?.detail;
      let i18nKey;

      if (integrationOwner && detail) {
        i18nKey =
          "neetoMolecules.auditLogs.text.integrationDisconnectedOnBehalf";
      } else if (integrationOwner) {
        i18nKey =
          "neetoMolecules.auditLogs.text.integrationDisconnectedOnBehalfWithoutDetail";
      } else if (detail) {
        i18nKey = "neetoMolecules.auditLogs.text.integrationDisconnected";
      } else {
        i18nKey =
          "neetoMolecules.auditLogs.text.integrationDisconnectedWithoutDetail";
      }

      return (
        <Trans
          {...{ i18nKey }}
          additionalValues={{
            performedBy,
            integrationName,
            detail,
            integrationOwner,
          }}
        />
      );
    }

    case ACTIONS.INTEGRATION_DISCONNECTED_BY_PROVIDER: {
      const { integrationName: rawName } = rest;
      const integrationName =
        INTEGRATION_DISPLAY_NAME_MAP[rawName] || humanize(rawName);
      const detail = changes?.detail;
      const i18nKey = detail
        ? "neetoMolecules.auditLogs.text.integrationDisconnectedByProvider"
        : "neetoMolecules.auditLogs.text.integrationDisconnectedByProviderWithoutDetail";

      return (
        <Trans
          {...{ i18nKey }}
          additionalValues={{ performedBy, integrationName, detail }}
        />
      );
    }

    case ACTIONS.CONNECTED_STRIPE_ACCOUNT: {
      return (
        <Trans
          i18nKey="neetoMolecules.auditLogs.text.connectedStripeAccount"
          additionalValues={{
            performedBy,
            stripeAccountId: changes.stripeAccountId,
          }}
        />
      );
    }

    case ACTIONS.DISCONNECTED_STRIPE_ACCOUNT: {
      return (
        <Trans
          additionalValues={{ performedBy }}
          i18nKey="neetoMolecules.auditLogs.text.disconnectedStripeAccount"
        />
      );
    }

    case ACTIONS.CONNECTED_ZOOM_INTEGRATION: {
      return (
        <Trans
          i18nKey="neetoMolecules.auditLogs.text.connectedZoomAccount"
          additionalValues={{
            performedBy,
            zoomAccountId: changes.zoomAccountId,
          }}
        />
      );
    }

    case ACTIONS.DISCONNECTED_ZOOM_INTEGRATION: {
      return (
        <Trans
          additionalValues={{ performedBy }}
          i18nKey="neetoMolecules.auditLogs.text.disconnectedZoomAccount"
        />
      );
    }

    case ACTIONS.CONNECTED_CUSTOM_DOMAIN: {
      return (
        <Trans
          additionalValues={{ performedBy, hostname: changes.hostname }}
          i18nKey="neetoMolecules.auditLogs.text.connectedCustomDomain"
        />
      );
    }

    case ACTIONS.DELETED_ROLE: {
      const roleName = organizationRole || "role";

      return (
        <Trans
          additionalValues={{ performedBy, roleName }}
          i18nKey="neetoMolecules.auditLogs.text.deletedRole"
        />
      );
    }

    case ACTIONS.CREATED_API_KEY: {
      return (
        <Trans
          additionalValues={{ performedBy, ...rest }}
          i18nKey="neetoMolecules.auditLogs.text.createdApiKey"
        />
      );
    }

    case ACTIONS.DELETED_API_KEY: {
      return (
        <Trans
          additionalValues={{ performedBy, ...rest }}
          i18nKey="neetoMolecules.auditLogs.text.deletedApiKey"
        />
      );
    }

    case ACTIONS.UPDATED_API_KEY: {
      return (
        <Trans
          additionalValues={{ performedBy, ...rest }}
          i18nKey="neetoMolecules.auditLogs.text.updatedApiKey"
        />
      );
    }

    default: {
      return (
        <Trans
          additionalValues={{ performedBy }}
          i18nKey="neetoMolecules.auditLogs.text.performedAction"
        />
      );
    }
  }
};

export const indexById = indexBy(prop("id"));

export const indexByPermissionName = indexBy(prop("name"));
